import { motion } from "framer-motion";
import throttle from "lodash/throttle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useViewport } from "src/contexts";
import { Icon, ImagePlaceholder } from "~components";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

/**
 * Compare tile aspect (W/H) to the loaded image (`naturalWidth / naturalHeight`). If they diverge
 * enough, `cover` would crop heavily — use `contain` + blurred fill instead.
 *
 * `mismatch = max(containerAR / imageAR, imageAR / containerAR)` (≥ 1). **Contain** when
 * `mismatch >=` this constant. **Higher** value → tolerate more `cover` cropping; **lower** →
 * switch to `contain` sooner (more empty space around the image, filled here by the blurred layer).
 */
const ASPECT_MISMATCH_CONTAIN_THRESHOLD = 1.8;

export type ImageMosaicTile = {
  id: string;
  imageUrl: string;
  /** Alt text and pill copy */
  name?: string;
  onClick?: () => void;
  /** Disables the onClick handler for optionally readonly forms */
  readonly?: boolean;
  isReversed?: boolean;
};

export type ImageMosaicDisplayProps = {
  tiles: ImageMosaicTile[];
};

/** A 1-2 column image grid layout which displays the first image as a "hero" span given an odd number of image tiles */
export function ImageMosaicDisplay(props: ImageMosaicDisplayProps) {
  const { tiles } = props;
  const { isMobile } = useViewport();
  const tileCount = tiles.length;

  // At most 2 columns: 1 col on mobile or ≤2 tiles; 2 cols on desktop with 3+ tiles.
  const columnCount = useMemo(() => (isMobile || tileCount <= 2 ? 1 : 2), [isMobile, tileCount]);

  // For most cases, we just allow each tile to be placed into the grid using the default grid behavior,
  // which chooses the next available row/column.
  // However, for odd tile counts (3 or more), instead of having a single tile that wraps to its own row,
  // we make the first tile span the full width on the first row for more balanced wrapping.
  const getTileGridColumn = useCallback(
    (index: number) => {
      if (index !== 0) return undefined;
      if (columnCount !== 2 || tileCount < 3 || tileCount % 2 === 0) return undefined;
      return "1 / -1";
    },
    [columnCount, tileCount],
  );

  if (tiles.isEmpty) return <ImagePlaceholder />;

  const gridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;

  return (
    <div css={Css.df.fdc.fg1.w100.mh0.h100.mw0.relative.oh.bgOffWhite.$}>
      <div css={Css.dg.h100.w100.maxw100.mh0.mw0.fg1.gapPx(1).gtc(gridTemplateColumns).gar("minmax(0, 1fr)").$}>
        {tiles.map((tile, index) => (
          <MosaicTile key={tile.id} gridColumn={getTileGridColumn(index)} tile={tile} />
        ))}
      </div>
    </div>
  );
}

function MosaicTile({ tile, gridColumn }: { tile: ImageMosaicTile; gridColumn: string | undefined }) {
  const [imageLoading, setImageLoading] = useState(true);
  // naturalWidth / naturalHeight after image is decoded; until then we only use `cover`.
  const [intrinsicAspectRatio, setIntrinsicAspectRatio] = useState<number | undefined>(undefined);
  const { imageUrl, name, onClick, isReversed, readonly } = tile;
  const tid = useTestIds({}, "mosaicTile");
  const { containerRef, useContain } = useMosaicTileAspectContain(intrinsicAspectRatio);

  // New Image → reset until the new img loads.
  useEffect(() => {
    setIntrinsicAspectRatio(undefined);
    setImageLoading(true);
  }, [imageUrl]);

  const maybeOnClick = readonly ? undefined : onClick;

  const reverseTransform = isReversed ? "scaleX(-1)" : undefined;
  const backdropTransform = isReversed ? "translateZ(0) scale(1.12) scaleX(-1)" : "translateZ(0) scale(1.12)";

  return (
    <div
      ref={containerRef}
      onClick={maybeOnClick}
      css={{
        // Relative: positioning context for the absolute img.
        // mw0/mh0: grid can shrink the cell.
        ...Css.relative.w100.h100.mw0.mh0.oh.$,
        ...(gridColumn ? Css.gc(gridColumn).$ : Css.$),
        ...Css.if(!!maybeOnClick).cursorPointer.$,
      }}
      {...tid}
    >
      {/* With `contain`, the image may not fill the tile -> blurred duplicate fills the gaps. */}
      {useContain && (
        <img
          alt=""
          aria-hidden
          src={imageUrl}
          css={{
            ...Css.absolute.top0.left0.right0.bottom0.w100.h100.objectCover.z0
              .add("filter", "blur(28px)")
              .add("transform", backdropTransform)
              .add("opacity", 0.92).$,
          }}
        />
      )}
      <img
        onLoad={(e) => {
          setImageLoading(false);
          const { naturalWidth, naturalHeight } = e.currentTarget;
          if (naturalWidth > 0 && naturalHeight > 0) {
            setIntrinsicAspectRatio(naturalWidth / naturalHeight);
          }
        }}
        css={{
          ...Css.absolute.top0.left0.right0.bottom0.w100.h100.$,
          // `cover`: fill tile, crop overflow. `contain`: show full image; may leave empty bands (blurred underlay above).
          ...(useContain ? Css.objectContain.z1.$ : Css.objectCover.$),
          ...(reverseTransform ? Css.add("transform", reverseTransform).$ : Css.$),
        }}
        src={imageUrl}
        alt={name}
      />
      {/* Empty full-tile layer on top of the img (sibling, not a wrapper): solid bg until load, then fades out via motion. */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.top0.left0.right0.bottom0.bgLightBlue.z2.$}
        animate={
          !imageLoading && {
            opacity: 0,
            transition: { duration: 0.4 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      />
      {name && (
        // Absolute positioning above bottom left corner of image
        <div css={Css.absolute.z3.bottom2.left2.$}>
          <span css={Css.df.aic.gap1.t12.fw400.gray900.bgWhite.o(0.8).px2.py1.$}>
            <span>{name}</span>
            <Icon name="arrow-next" size={14} />
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Exposes `containerRef` for the tile root and `useContain`: whether tile vs loaded-image aspect
 * warrants `object-fit: contain` (see `shouldUseContainForAspectMismatch` below). Inactive until
 * `intrinsicAspectRatio` is known. Subscribes to size changes via ResizeObserver; throttled to limit
 * re-renders while the layout is changing.
 */
function useMosaicTileAspectContain(intrinsicAspectRatio: number | undefined) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [useContain, setUseContain] = useState(false);

  /** True → prefer `contain` over `cover` for this tile size and image aspect. */
  const shouldUseContainForAspectMismatch = useCallback((width: number, height: number, imageAspectRatio: number) => {
    if (width <= 0 || height <= 0 || imageAspectRatio <= 0) return false;
    const containerAR = width / height;
    // Compare “how wide vs tall” the tile is to the image, both ways. One ratio is >1 when the
    // container is wider (per unit height) than the image; the other is >1 when the image is wider
    // (per unit height) than the container. Take the max so we always measure the stronger skew
    // (symmetric: same result if you swap which is “container” vs “image” in the formula).
    const mismatch = Math.max(containerAR / imageAspectRatio, imageAspectRatio / containerAR);
    return mismatch >= ASPECT_MISMATCH_CONTAIN_THRESHOLD;
  }, []);

  const throttledCalculateContain = useMemo(
    () =>
      throttle((width: number, height: number) => {
        if (width <= 0 || height <= 0) return;

        if (intrinsicAspectRatio == null || intrinsicAspectRatio <= 0) {
          setUseContain(false);
          return;
        }

        setUseContain(shouldUseContainForAspectMismatch(width, height, intrinsicAspectRatio));
      }, 50),
    [intrinsicAspectRatio, shouldUseContainForAspectMismatch],
  );

  useEffect(() => {
    // No intrinsic size yet → always `cover`; cancel any scheduled trailing throttle call.
    if (intrinsicAspectRatio == null || intrinsicAspectRatio <= 0) {
      setUseContain(false);
      throttledCalculateContain.cancel();
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      // Content box matches how the tile allocates space for the absolutely positioned <img>.
      const { width, height } = entry.contentRect;
      throttledCalculateContain(width, height);
    });

    resizeObserver.observe(container);

    // Initial measure: RO may not fire on subscribe alone.
    const { width, height } = container.getBoundingClientRect();
    throttledCalculateContain(width, height);

    return () => {
      resizeObserver.disconnect();
      throttledCalculateContain.cancel();
    };
  }, [intrinsicAspectRatio, throttledCalculateContain]);

  return { containerRef, useContain };
}
